import React, { useRef, useEffect, useCallback } from 'react';
import { EditorView } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { createEditorExtensions } from './editorSetup';

const MarkdownEditor = ({ content, onChange, onSave }) => {
  const editorRef = useRef(null);
  const viewRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onSaveRef = useRef(onSave);

  // Keep refs updated
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onSaveRef.current = onSave; }, [onSave]);

  useEffect(() => {
    if (!editorRef.current) return;

    const extensions = createEditorExtensions(() => onSaveRef.current?.());

    const state = EditorState.create({
      doc: content || '',
      extensions: [
        ...extensions,
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            onChangeRef.current?.(update.state.doc.toString());
          }
        }),
      ],
    });

    const view = new EditorView({
      state,
      parent: editorRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update content from outside (e.g., loading document)
  const setContent = useCallback((newContent) => {
    const view = viewRef.current;
    if (!view) return;
    const currentContent = view.state.doc.toString();
    if (currentContent !== newContent) {
      view.dispatch({
        changes: { from: 0, to: currentContent.length, insert: newContent }
      });
    }
  }, []);

  // Expose setContent for external use
  useEffect(() => {
    if (viewRef.current && content !== undefined) {
      const currentContent = viewRef.current.state.doc.toString();
      if (currentContent !== content && content !== null) {
        if (!viewRef.current.hasFocus) {
          setContent(content);
        }
      }
    }
  }, [content, setContent]);

  return (
    <div
      ref={editorRef}
      className="bear-cm-container"
      style={{ flex: 1, overflow: 'hidden' }}
    />
  );
};

export default MarkdownEditor;
